import { ConstraintDocument } from 'types/ConstraintProfileTypes';
import { diffDocumentPaths } from 'utils/constraintDocument';
import {
  sectionGroups,
  visibleConstraintSectionTypes,
  type ConstraintsPanelMode,
} from './panelSections';

/**
 * The `group.field` paths the agent changed between two working Documents.
 * These are the cells that get the orange border / drive the section red dot
 * (spec §5.2 — the agent-provenance signal).
 */
export function agentChangedFieldIds(
  before: ConstraintDocument,
  after: ConstraintDocument,
): string[] {
  return diffDocumentPaths(before, after);
}

/** Whether a panel section has any unacknowledged agent-changed path,
 *  across every real group the section owns. A section dot is suppressed
 *  if every real group it owns has been acknowledged via
 *  `acknowledgeSection`, regardless of how many field-level paths still
 *  carry orange borders inside. The two memories are independent on
 *  purpose: opening the accordion clears the section dot; typing over
 *  each field clears that field's orange. */
export function sectionHasAgentChange(
  sectionKey: string,
  changedPaths: string[],
  acknowledgedGroups: string[] = [],
): boolean {
  const groups = sectionGroups(sectionKey);
  const liveGroups = groups.filter((g) => !acknowledgedGroups.includes(g));
  if (liveGroups.length === 0) return false;
  return changedPaths.some((path) =>
    liveGroups.some((g) => path === g || path.startsWith(`${g}.`)),
  );
}

// Does any visible section currently have a live agent-change dot? Drives
// the Vincoli menu-button red dot, so it stays on until every section
// inside the panel is acknowledged.
export function hasAnyVisibleSectionAgentChange(
  mode: ConstraintsPanelMode,
  changedPaths: string[],
  acknowledgedGroups: string[],
): boolean {
  return visibleConstraintSectionTypes(mode).some((type) =>
    sectionHasAgentChange(type, changedPaths, acknowledgedGroups),
  );
}
